import { EMPTY_ARRAY, isNil } from "client/Misc/utils";
import { concat } from "client_legacy/ndaw/arrayHelpers";
import { ensureNotCancelled } from "client_legacy/ndaw/cancellation";
import { InputValidationResult, InputValidationResultKind } from "./input";
import {
	convertToRuleTriggerParams,
	executeRule,
	RuleTriggerParams
} from "./ruleRunner";
import { IValidationContext, IValidationRule } from "./rules";
import { ConversionRule } from "./rules/misc";
import {
	getDefaultNodeState,
	NodeValidationState,
	ValidationRuleState
} from "./state";
import { ExecuteValidationParams } from "./validationRunner";
import { ModelPath } from "./path";
import { ConversionError } from "client/Forms/converters";

export type UniqueKeyGenerator<TValue> = (item: TValue, index: number) => string;

export type ValidatorExecutionScope<TValue> = {
	value: TValue;
	parentValue: unknown;
	stablePath: string;
	modelPath: ModelPath;
};

export interface IValidator<TValue> {
	execute(
		currentScope: ValidatorExecutionScope<TValue>,
		params: ExecuteValidationParams,
		nodeStates: readonly NodeValidationState[],
		conversionErrorMap: ReadonlyMap<string, ConversionError>,
		signal?: AbortSignal
	): Promise<readonly NodeValidationState[]>;

	getMatchingStablePaths(
		currentScope: ValidatorExecutionScope<TValue>,
		lookupModelPath: ModelPath
	): readonly string[];
}

export class NodeValidator<TValue> implements IValidator<TValue> {
	private readonly _conversionRule: ConversionRule<TValue> | undefined;
	private readonly _rules: readonly IValidationRule<TValue>[];
	private readonly _children: readonly IValidator<TValue>[];

	constructor(
		rules: readonly IValidationRule<TValue>[],
		children: readonly IValidator<TValue>[]
	) {
		this._conversionRule = rules.find(r => r instanceof ConversionRule);

		this._rules = rules.filter(r => !(r instanceof ConversionRule));

		this._children = children;
	}

	public async execute(
		currentScope: ValidatorExecutionScope<TValue>,
		params: ExecuteValidationParams,
		currentNodeStates: readonly NodeValidationState[],
		conversionErrorMap: ReadonlyMap<string, ConversionError>,
		signal?: AbortSignal
	): Promise<readonly NodeValidationState[]> {
		const {
			value,
			parentValue,
			stablePath,
			modelPath
		} = currentScope;

		const currentState: NodeValidationState = currentNodeStates.find(s => s.stablePath === stablePath)
			?? getDefaultNodeState(
				stablePath,
				modelPath,
				this._rules.length
			);

		const ruleTriggerParams: RuleTriggerParams = convertToRuleTriggerParams(modelPath, params);

		const ruleExecutions: Promise<ValidationRuleState>[] = [];

		let newRuleStates: ValidationRuleState[];
		let newConversionRuleState: ValidationRuleState;

		const validationContext: IValidationContext = {
			parentValue
		};

		const conversionError: ConversionError<unknown> | undefined = conversionErrorMap.get(stablePath);

		if (conversionError) {
			if (this._conversionRule) {
				newConversionRuleState = await executeRule(
					conversionError,
					validationContext,
					this._conversionRule,
					ruleTriggerParams,
					currentState.conversionRuleState,
					signal
				);
			} else {
				newConversionRuleState = {
					isActivated: true,
					result: {
						kind: InputValidationResultKind.Valid
					}
				};
			}
			ensureNotCancelled(signal);

			newRuleStates = [...currentState.ruleStates];
		} else {
			for (let i = 0; i < this._rules.length; i++) {
				const rule: IValidationRule<TValue> = this._rules[i];
				const previousRuleState: ValidationRuleState | undefined = currentState.ruleStates[i];

				const ruleExecution: Promise<ValidationRuleState> = executeRule(
					value,
					validationContext,
					rule,
					ruleTriggerParams,
					previousRuleState,
					signal
				);

				ruleExecutions.push(ruleExecution);
			}

			newRuleStates = await Promise.all(ruleExecutions);
			ensureNotCancelled(signal);
			newConversionRuleState = {
				isActivated: false,
				result: {
					kind: InputValidationResultKind.Valid
				}
			};
		}

		const childValidationExecutions: Promise<readonly NodeValidationState[]>[] = [];

		for (const child of this._children) {
			const childValidationExecution: Promise<readonly NodeValidationState[]> = child.execute(
				currentScope,
				params,
				currentNodeStates,
				conversionErrorMap,
				signal
			);

			childValidationExecutions.push(childValidationExecution);
		}

		const childValidationStates: readonly NodeValidationState[] = concat(await Promise.all(childValidationExecutions));
		ensureNotCancelled(signal);

		const ownResultValidationState: NodeValidationState = {
			...currentState,
			modelPath,
			clientResult: this.calculateNewClientValidationResult(
				newConversionRuleState,
				newRuleStates
			),
			conversionRuleState: newConversionRuleState,
			ruleStates: newRuleStates
		};

		return [
			ownResultValidationState,
			...childValidationStates
		];
	}

	public getMatchingStablePaths(
		currentScope: ValidatorExecutionScope<TValue>,
		lookupModelPath: ModelPath
	): readonly string[] {
		const {
			modelPath,
			stablePath
		} = currentScope;

		const doesMatchLookupPath: boolean = modelPath.equals(lookupModelPath);
		const isAncestorOfLookupPath: boolean = modelPath.isAncestorOf(lookupModelPath);

		if (!doesMatchLookupPath && !isAncestorOfLookupPath) {
			return EMPTY_ARRAY;
		}

		const childrenResults: string[] = this._children.flatMap(
			c => c.getMatchingStablePaths(
				currentScope,
				lookupModelPath
			)
		);

		return doesMatchLookupPath
			? [
				stablePath,
				...childrenResults
			]
			: childrenResults;
	}

	private calculateNewClientValidationResult(
		conversionRuleState: ValidationRuleState,
		ruleStates: readonly ValidationRuleState[],
	): InputValidationResult {
		if (conversionRuleState.result.kind === InputValidationResultKind.Invalid) {
			return conversionRuleState.result;
		}

		for (const ruleState of ruleStates) {
			if (ruleState.result.kind === InputValidationResultKind.Invalid) {
				return ruleState.result;
			}
		}

		return {
			kind: InputValidationResultKind.Valid
		};
	}
}

export class PropertyValidator<TParent, TProperty extends StringKeyOf<TParent>> implements IValidator<TParent> {
	public readonly propertyName: TProperty;
	public readonly childValidator: IValidator<TParent[TProperty]>;

	constructor(
		propertyName: TProperty,
		childValidator: IValidator<TParent[TProperty]>,
	) {
		this.propertyName = propertyName;
		this.childValidator = childValidator;
	}

	public execute(
		currentScope: ValidatorExecutionScope<TParent>,
		params: ExecuteValidationParams,
		nodeStates: readonly NodeValidationState[],
		conversionErrorMap: ReadonlyMap<string, ConversionError>,
		signal?: AbortSignal
	): Promise<readonly NodeValidationState[]> {
		const {
			value
		} = currentScope;

		if (isNil(value)) {
			return Promise.resolve(EMPTY_ARRAY);
		}

		return this.childValidator.execute(
			this.getChildScope(currentScope),
			params,
			nodeStates,
			conversionErrorMap,
			signal
		);
	}

	public getMatchingStablePaths(
		currentScope: ValidatorExecutionScope<TParent>,
		lookupModelPath: ModelPath
	): readonly string[] {
		const {
			value
		} = currentScope;

		if (isNil(value)) {
			return EMPTY_ARRAY;
		}

		return this.childValidator.getMatchingStablePaths(
			this.getChildScope(currentScope),
			lookupModelPath
		);
	}

	private getChildScope(currentScope: ValidatorExecutionScope<TParent>) {
		const {
			value,
			stablePath,
			modelPath
		} = currentScope;

		const propertyStablePath: string = `${stablePath}.${this.propertyName}`;

		return {
			value: this.getPropertyValue(value),
			parentValue: value,
			stablePath: propertyStablePath,
			modelPath: modelPath.for(this.propertyName)
		};
	}

	private getPropertyValue(parent: TParent): TParent[TProperty] {
		return parent[this.propertyName];
	}
}

export class CollectionItemValidator<TCollectionItem> implements IValidator<readonly TCollectionItem[]> {
	public readonly childValidator: IValidator<TCollectionItem>;
	private readonly _uniqueKeyGenerator: UniqueKeyGenerator<TCollectionItem>;

	constructor(
		childValidator: IValidator<TCollectionItem>,
		uniqueKeyGenerator?: UniqueKeyGenerator<TCollectionItem>
	) {
		this.childValidator = childValidator;
		this._uniqueKeyGenerator = uniqueKeyGenerator ?? (
			(_, index) => index.toString()
		);
	}

	public async execute(
		currentScope: ValidatorExecutionScope<readonly TCollectionItem[]>,
		params: ExecuteValidationParams,
		nodeStates: readonly NodeValidationState[],
		conversionErrorMap: ReadonlyMap<string, ConversionError>,
		signal?: AbortSignal
	): Promise<readonly NodeValidationState[]> {
		const {
			value
		} = currentScope;

		if (isNil(value)) {
			return Promise.resolve([]);
		}

		const existingKeys = new Set<string>();

		const childValidationExecutions: Promise<readonly NodeValidationState[]>[] = [];
		for (let i = 0; i < value.length; i++) {
			const childValidationExecution: Promise<readonly NodeValidationState[]> = this.childValidator.execute(
				this.getChildScope(currentScope, i, existingKeys),
				params,
				nodeStates,
				conversionErrorMap,
				signal
			);
			childValidationExecutions.push(childValidationExecution);
		}

		return concat(await Promise.all(childValidationExecutions));
	}

	public getMatchingStablePaths(
		currentScope: ValidatorExecutionScope<readonly TCollectionItem[]>,
		lookupModelPath: ModelPath
	): readonly string[] {
		const {
			value
		} = currentScope;

		const result: string[] = [];
		const existingKeys = new Set<string>();

		for (let i = 0; i < value.length; i++) {
			const childResults: readonly string[] = this.childValidator.getMatchingStablePaths(
				this.getChildScope(
					currentScope,
					i,
					existingKeys
				),
				lookupModelPath
			);

			result.push(...childResults);
		}

		return result;
	}

	private getChildScope(
		currentScope: ValidatorExecutionScope<readonly TCollectionItem[]>,
		index: number,
		existingKeys: Set<string>
	) {

		const {
			value,
			stablePath,
			modelPath
		} = currentScope;

		const item: TCollectionItem = value[index];
		let uniqueKey: string = escapeUniqueKey(this._uniqueKeyGenerator(item, index));

		if (existingKeys.has(uniqueKey)) {
			console.error(`Key ${uniqueKey} is not unique in collection.`);
			uniqueKey = this.getActuallyUniqueKey(uniqueKey, existingKeys);
		}

		existingKeys.add(uniqueKey);

		const collectionItemStablePath: string = `${stablePath}[${uniqueKey}]`;

		return {
			value: item,
			parentValue: value,
			stablePath: collectionItemStablePath,
			modelPath: modelPath.forIndex(index)
		};
	}

	private getActuallyUniqueKey(notFullyUniqueKey: string, existingKeys: Set<string>) {
		for (let i = 2; i < 100; i++) {
			const candidate: string = `${notFullyUniqueKey}_${i}`;
			if (!existingKeys.has(candidate)) {
				return candidate;
			}
		}

		throw new Error("Unable to create a unique key");
	}
}

export class ConditionalValidator<TValue> implements IValidator<TValue> {
	private readonly _index: number;
	private readonly _predicate: (value: TValue) => boolean;
	private readonly _trueValidator: IValidator<TValue>;
	private readonly _falseValidator: IValidator<TValue> | undefined;

	constructor(
		index: number,
		predicate: (value: TValue) => boolean,
		trueValidator: IValidator<TValue>,
		falseValidator: IValidator<TValue> | undefined
	) {
		this._index = index;
		this._predicate = predicate;
		this._trueValidator = trueValidator;
		this._falseValidator = falseValidator;
	}

	public execute(
		currentScope: ValidatorExecutionScope<TValue>,
		params: ExecuteValidationParams,
		nodeStates: readonly NodeValidationState[],
		conversionErrorMap: ReadonlyMap<string, ConversionError>,
		signal?: AbortSignal
	): Promise<readonly NodeValidationState[]> {
		const {
			value
		} = currentScope;

		if (this._predicate(value)){
			return this._trueValidator
				.execute(
					this.getChildScope(currentScope, true),
					params,
					nodeStates,
					conversionErrorMap,
					signal
				);
		} else if (this._falseValidator) {
			return this._falseValidator
				.execute(
					this.getChildScope(currentScope, false),
					params,
					nodeStates,
					conversionErrorMap,
					signal
				);
		}

		return Promise.resolve([]);
	}

	public getMatchingStablePaths(
		currentScope: ValidatorExecutionScope<TValue>,
		lookupModelPath: ModelPath
	): readonly string[] {
		const { value } = currentScope;

		if (this._predicate(value)){
			return this._trueValidator
				.getMatchingStablePaths(
					this.getChildScope(
						currentScope,
						true
					),
					lookupModelPath
				);
		} else if (this._falseValidator) {
			return this._falseValidator
				.getMatchingStablePaths(
					this.getChildScope(
						currentScope,
						false
					),
					lookupModelPath
				);
		}

		return EMPTY_ARRAY;
	}

	private getChildScope(
		currentScope: ValidatorExecutionScope<TValue>,
		branch: boolean
	): ValidatorExecutionScope<TValue> {
		const {
			stablePath,
			...restOfTheScope
		} = currentScope;

		const resultStablePath: string = branch
			? `${stablePath}[true?${this._index}]`
			: `${stablePath}[false?${this._index}]`;

		return {
			...restOfTheScope,
			stablePath: resultStablePath
		};
	}
}

export class AdditionalValidator<TValue> implements IValidator<TValue> {
	private readonly _actualValidator: IValidator<TValue>;
	private readonly _index: number;

	constructor(
		actualValidator: IValidator<TValue>,
		index: number
	) {
		this._actualValidator = actualValidator;
		this._index = index;
	}

	public execute(
		currentScope: ValidatorExecutionScope<TValue>,
		params: ExecuteValidationParams,
		nodeStates: readonly NodeValidationState[],
		conversionErrorMap: ReadonlyMap<string, ConversionError>,
		signal?: AbortSignal
	): Promise<readonly NodeValidationState[]> {
		return this._actualValidator.execute(
			this.getChildScope(currentScope),
			params,
			nodeStates,
			conversionErrorMap,
			signal
		);
	}

	public getMatchingStablePaths(
		currentScope: ValidatorExecutionScope<TValue>,
		lookupModelPath: ModelPath
	): readonly string[] {
		return this._actualValidator.getMatchingStablePaths(
			this.getChildScope(currentScope),
			lookupModelPath
		);
	}


	private getChildScope(
		currentScope: ValidatorExecutionScope<TValue>
	): ValidatorExecutionScope<TValue> {
		const {
			stablePath,
			...restOfTheScope
		} = currentScope;

		const resultStablePath: string = `${stablePath}[additional?${this._index}]`;

		return {
			...restOfTheScope,
			stablePath: resultStablePath
		};
	}
}

function escapeUniqueKey(uniqueKey: string): string {
	return uniqueKey.replace("%", "%0")
		.replace(".", "%1")
		.replace("[", "%2")
		.replace("]", "%3");
}